<template>
  <tbody
    :id="transactionDomID"
    data-automation="activity-row"
  >
    <tr :class="{ error : transactionItem.result === 1 }">
      <td
        v-if="enableExpansion"
        class="icon"
      >
        <div class="actionBar inline">
          <ActionButton
            :button-class="toggleActionClass"
            label="details"
            :data-automation="'activity-detail-button-' + transactionItem.id"
            @doIt="toggleExpanded"
          />
        </div>
      </td>
      <td data-automation="activity-action">
        {{ transactionItem.action }}
      </td>
      <td data-automation="activity-published-time">
        {{ timeCreatedFormatted }}
      </td>
      <td>{{ duration }}</td>
      <td data-automation="activity-status">
        {{ statusText }}
      </td>
      <td
        class="spanned"
        data-automation="activity-added"
      >
        <ZeroDash :value="transactionItem.packages_added" />
      </td>
      <td
        v-if="!python"
        class="spanned"
        data-automation="activity-archived"
      >
        <ZeroDash :value="transactionItem.packages_archived" />
      </td>
      <td
        v-else
        class="spanned"
        data-automation="activity-updated"
      >
        <ZeroDash :value="transactionItem.packages_updated" />
      </td>
      <td
        class="spanned"
        data-automation="activity-deleted"
      >
        <ZeroDash :value="transactionItem.packages_removed" />
      </td>

      <td
        class="finePrint"
        data-automation="activity-id"
      >
        {{ transactionItem.id }}
      </td>
    </tr>
    <tr
      v-if="showResultsHeader"
      class="internalHeader"
    >
      <th />
      <th
        colspan="2"
        data-automation="activity-header-package"
      >
        Package
      </th>
      <th data-automation="activity-header-version">
        Version
      </th>
      <th data-automation="activity-header-action">
        Action
      </th>
      <th colspan="6" />
    </tr>
    <tr
      v-for="r in resultsRows"
      :key="r.name"
      class="internalRow"
      data-automation="activity-action-row"
      :value="r.name"
    >
      <td />
      <td colspan="2">
        <router-link
          class="standardLink"
          data-automation="activity-pkgname"
          :to="{ name: 'package', params: { repoId: currentRepoId, packageName: r.name}}"
        >
          {{ r.name }}
        </router-link>
      </td>
      <td data-automation="activity-pkgversion">
        {{ r.version }}
      </td>
      <td data-automation="activity-pkgaction">
        {{ r.action }}
      </td>
      <td class="spanned">
        <span
          v-if="r.action === 'add'"
          data-automation="activity-add-check"
        >&#10003;</span>
      </td>
      <td class="spanned">
        <span
          v-if="r.action === 'archive'"
          data-automation="activity-archive-check"
        >&#10003;</span>
      </td>
      <td class="spanned">
        <span
          v-if="r.action === 'delete'"
          data-automation="activity-delete-check"
        >&#10003;</span>
      </td>
      <td />
    </tr>
    <tr v-if="expanded && gettingPackages">
      <td
        colspan="10"
        style="padding: 0"
      >
        <GettingMore label="Getting package transactions" />
      </td>
    </tr>
    <tr v-if="expanded && showGetNextPackages">
      <td
        colspan="10"
        style="padding: 0"
      >
        <GetMoreButton
          label="Get more package transactions"
          @getMore="getNextTransactionPackages"
        />
      </td>
    </tr>
    <tr
      v-if="showNoResults"
      class="detailsRow"
    >
      <td colspan="10">
        <span>No package transactions found</span>
      </td>
    </tr>
    <tr
      v-if="expanded && transactionItem.result === 1 && !gettingDetails"
      class="detailsRow"
    >
      <td
        colspan="10"
        style="word-wrap: break-word;"
      >
        <span v-if="transactionItem.hasOwnProperty('transactionDetails')">{{ transactionItem.transactionDetails.details }}</span>
      </td>
    </tr>
    <tr
      v-if="expanded && transactionItem.tag && transactionItem.action !== 'Sync' && !gettingPackages"
      class="commentRow"
    >
      <td colspan="10">
        {{ transactionItem.tag }}
      </td>
    </tr>
    <tr
      v-if="expanded && gettingDetails"
      class="detailsRow"
    >
      <td
        colspan="10"
        style="word-wrap: break-word; text-align:center;"
      >
        <span><div class="detailsSpinnerContainer"><Spinner /></div>Getting details</span>
      </td>
    </tr>
  </tbody>
</template>

<script>

import { mapState } from 'vuex'
import {
  parseISO, differenceInDays, differenceInHours, differenceInMinutes,
  differenceInSeconds, differenceInMilliseconds,
} from 'date-fns'

import ActionButton from './ActionButton'
import Spinner from './Spinner'
import ZeroDash from './ZeroDash'
import GetMoreButton from './GetMoreButton'
import GettingMore from './GettingMore'
import { parseTimeToString } from '../utils/time'

export default {
  name: 'TransactionListItem',
  components: {
    ActionButton,
    Spinner,
    ZeroDash,
    GettingMore,
    GetMoreButton,
  },
  props: {
    transactionItem: {
      type: Object,
      required: true,
    },
    now: {
      type: Date,
      required: true,
      default: Date.now(),
    },
    enableExpansion: {
      type: Boolean,
      required: false,
      default: true,
    },
    python: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data: function () {
    return {
      expanded: false,
    }
  },
  computed: {
    ...mapState({
      currentRepoId: state => state.repos.currentRepoId,
      currentRepoSource: state => state.repos.currentRepoSource,
      gettingTransactionDetails: state => state.transactions.gettingTransactionDetails,
      gettingTransactionPackages: state => state.transactions.gettingTransactionPackages,
    }),
    duration: function () {
      const end = this.transactionItem.completed ? parseISO(this.transactionItem.completed) : this.now
      const start = parseISO(this.transactionItem.created)
      const dms = differenceInMilliseconds(end, start)
      if (dms < 1000) {
        return '1 second'
      } else {
        const ds = differenceInSeconds(end, start)
        if (ds < 60) {
          return ds + (ds > 1 ? ' seconds' : ' second')
        } else {
          const dm = differenceInMinutes(end, start)
          if (dm < 60) {
            return dm + (dm > 1 ? ' minutes' : ' minute')
          } else {
            const dh = differenceInHours(end, start)
            if (dh < 24) {
              return dh + (dh > 1 ? ' hours' : ' hour')
            } else {
              const dd = differenceInDays(end, start)
              return dd + (dd > 1 ? ' days' : ' day')
            }
          }
        }
      }
    },
    statusText: function () {
      if (this.transactionItem.completed) {
        return this.transactionItem.result === 0 ? 'Completed' : 'Failed'
      }
      return 'In progress'
    },
    toggleActionClass: function () {
      let tac = 'toggle expandCollapse'
      if (this.expanded) {
        tac += ' toggled'
      }
      return tac
    },
    gettingDetails: function () {
      const gd = this.gettingTransactionDetails.find(td => { return td === this.transactionItem.id })
      return gd !== undefined
    },
    showDetails: function () {
      return this.expanded && this.transactionItem.result === 1 && !this.gettingDetails
    },
    gettingPackages: function () {
      const gd = this.gettingTransactionPackages.find(td => { return td === this.transactionItem.id })
      return gd !== undefined
    },
    timeCreatedFormatted: function () {
      const createdTime = this.transactionItem.published_to ? this.transactionItem.published_to : this.transactionItem.created
      return parseTimeToString(createdTime, this.showLocalTZ, { showTime: this.showLocalTZ, dateFormat: 'dash' })
    },
    showResultsHeader: function () {
      return this.expanded && this.transactionItem.result === 0 && this.transactionItem.packages && this.transactionItem.packages.length
    },
    resultsRows: function () {
      if (this.expanded) {
        return this.transactionItem.packages
      } else {
        return []
      }
    },
    showNoResults: function () {
      return this.expanded && this.transactionItem.result === 0 && this.transactionItem.packages && this.transactionItem.packages.length === 0
    },
    showGetNextPackages: function () {
      return !this.gettingPackages && (this.transactionItem.packages && this.transactionItem.packagesCount > this.transactionItem.packages.length)
    },
    transactionDomID: function () {
      return `e2e-transaction-${this.transactionItem.id}`
    },
  },
  methods: {
    pad: function (val) {
      if (val < 10) {
        return `0${val}`
      }
      return val
    },
    toggleExpanded: function () {
      this.expanded = !this.expanded
      if (this.expanded) {
        if (this.transactionItem.result === 0) {
          if (!Object.prototype.hasOwnProperty.call(this.transactionItem, 'packages') && !this.gettingPackages) {
            this.$store.dispatch('getFirstTransactionPackages', { sourceId: this.currentRepoSource, transactionId: this.transactionItem.id })
          }
        } else {
          if (!Object.prototype.hasOwnProperty.call(this.transactionItem, 'transactionDetails') && !this.gettingDetails) {
            this.$store.dispatch('getTransactionDetails', { sourceId: this.currentRepoSource, transactionId: this.transactionItem.id })
          }
        }
      }
    },
    getNextTransactionPackages: function () {
      this.$store.dispatch('getNextTransactionPackages', { sourceId: this.currentRepoSource, transactionId: this.transactionItem.id })
    },
  },
}

</script>

<style scoped>

.error {
  color: red;
}

</style>
